import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError, setStoredToken, getStoredToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip /me — AuthCallback exchanges first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Only call /me if we have either a cookie present OR a Bearer token in storage.
    // (We can't actually inspect httpOnly cookies, so always try — the interceptor adds Bearer if available.)
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Persist token as Bearer fallback (mobile Safari ITP blocks 3rd-party cookies)
    if (data?.token) setStoredToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data?.token) setStoredToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    // Best-effort server-side cookie clear; swallow is intentional (user is leaving).
    try { await api.post("/auth/logout"); } catch (e) { /* noop */ }
    setStoredToken(null);
    setUser(null);
  };

  const value = { user, loading, login, register, logout, refresh, apiError };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
